import type { Location } from "@/lib/api";

export type SavedRoute = {
	id: string;
	fromName: string;
	fromLat: number;
	fromLon: number;
	toName: string;
	toLat: number;
	toLon: number;
	savedAt: number;
};

const SAVED_KEY = "trw_saved_routes";
const RECENT_KEY = "trw_recent_searches";
const MAX_RECENT = 5;

function readList(key: string): SavedRoute[] {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? (parsed as SavedRoute[]) : [];
	} catch {
		return [];
	}
}

function writeList(key: string, routes: SavedRoute[]): void {
	try {
		localStorage.setItem(key, JSON.stringify(routes));
	} catch {
		// Storage unavailable — skip.
	}
}

function routeKey(from: Location, to: Location): string {
	return `${from.name}|${to.name}`;
}

export function listSavedRoutes(): SavedRoute[] {
	return readList(SAVED_KEY).sort((a, b) => b.savedAt - a.savedAt);
}

export function isRouteSaved(from: Location, to: Location): boolean {
	return readList(SAVED_KEY).some(
		(r) => r.fromName === from.name && r.toName === to.name,
	);
}

export function saveRoute(from: Location, to: Location): void {
	if (!from.name || !to.name) return;
	const routes = readList(SAVED_KEY).filter(
		(r) => !(r.fromName === from.name && r.toName === to.name),
	);
	routes.push({
		id: `${Date.now()}`,
		fromName: from.name,
		fromLat: from.latitude,
		fromLon: from.longitude,
		toName: to.name,
		toLat: to.latitude,
		toLon: to.longitude,
		savedAt: Date.now(),
	});
	writeList(SAVED_KEY, routes);
}

export function removeSavedRoute(from: Location, to: Location): void {
	writeList(
		SAVED_KEY,
		readList(SAVED_KEY).filter(
			(r) => !(r.fromName === from.name && r.toName === to.name),
		),
	);
}

export function recordRecentSearch(from: Location, to: Location): void {
	if (!from.name || !to.name) return;
	const entry: SavedRoute = {
		id: routeKey(from, to),
		fromName: from.name,
		fromLat: from.latitude,
		fromLon: from.longitude,
		toName: to.name,
		toLat: to.latitude,
		toLon: to.longitude,
		savedAt: Date.now(),
	};
	const rest = readList(RECENT_KEY).filter((r) => r.id !== entry.id);
	writeList(RECENT_KEY, [entry, ...rest].slice(0, MAX_RECENT));
}

export function listRecentSearches(): SavedRoute[] {
	return readList(RECENT_KEY);
}
